/**
 * Admin console › Permit equipment: the manufacturer PDFs plan sets append (docs/features/permit-plan-sets.md §12).
 * An equipment entry's datasheet is stored on the entry itself (`datasheet_file_id`, the same artifact path companies
 * use for their own equipment; the table's one "Datasheet" column). The racking catalogue's documents (each rail's
 * datasheet and span table, each attachment's datasheet and detail page, each rapid-shutdown device's datasheet)
 * live in `permit_documents`. Third-party
 * files: uploaded here, stored by the API, never in the repository. An upload replaces the item's live file; plan sets
 * generated before keep theirs.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { api, errorMessage, rawFetch, unwrap, type S } from "../../api/client";
import { Badge, Button, Card, Spinner, useToast } from "../../components/ui";
import { useT } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/keys";

type Doc = S["PermitDocument"];
type SubjectKind = S["PermitDocumentSubjectKind"];
type DocKind = S["PermitDocumentKind"];

export function usePermitDocuments() {
  return useQuery({ queryKey: ["admin-permit-documents"], queryFn: () => unwrap(api.GET("/v1/admin/permit-documents")) });
}

function useDocActions() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-permit-documents"] });
    qc.invalidateQueries({ queryKey: ["permit-racking"] });
    qc.invalidateQueries({ queryKey: ["plan-set-preview"] });
  };
  const upload = useMutation({
    mutationFn: async (v: { file: File; subject_kind: SubjectKind; subject_key: string; doc_kind: DocKind }) => {
      const form = new FormData();
      form.append("file", v.file);
      form.append("subject_kind", v.subject_kind);
      form.append("subject_key", v.subject_key);
      form.append("doc_kind", v.doc_kind);
      return (await rawFetch("/api/v1/admin/permit-documents", { method: "POST", body: form })).json() as Promise<Doc>;
    },
    onSuccess: (d) => {
      refresh();
      toast(t("admin.permitDocuments.uploaded", { file: d.file_name, pages: d.pages }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const remove = useMutation({
    mutationFn: (d: Doc) => rawFetch(d.url, { method: "DELETE" }),
    onSuccess: () => refresh(),
    onError: (e) => toast(errorMessage(e), true),
  });
  return { upload, remove };
}

async function openPdf(doc: Doc) {
  const blob = await (await rawFetch(doc.url)).blob();
  window.open(URL.createObjectURL(blob), "_blank", "noopener");
}

/** One document slot: view, replace or remove the live PDF, or upload one. */
export function DocumentSlot({ subjectKind, subjectKey, docKind, docs, label }: { subjectKind: SubjectKind; subjectKey: string; docKind: DocKind; docs: Doc[] | undefined; label?: MessageKey }) {
  const input = useRef<HTMLInputElement>(null);
  const { upload, remove } = useDocActions();
  const t = useT();
  const doc = docs?.find((d) => d.subject_kind === subjectKind && d.subject_key === subjectKey && d.doc_kind === docKind);
  return (
    <span className="row" style={{ gap: 4, flexWrap: "wrap", alignItems: "center" }}>
      {label && <span className="small muted">{t(label)}</span>}
      {doc ? (
        <>
          <Badge tone="ok">{t("admin.permitDocuments.pdfPages", { pages: doc.pages })}</Badge>
          <Button size="sm" variant="ghost" icon={<Eye size={14} />} aria-label={t("admin.permitDocuments.view", { file: doc.file_name })} onClick={() => void openPdf(doc)} />
          <Button size="sm" variant="ghost" icon={<Trash2 size={14} />} aria-label={t("admin.permitDocuments.remove")} loading={remove.isPending} onClick={() => remove.mutate(doc)} />
        </>
      ) : (
        <Badge tone="warn">{t("admin.permitDocuments.missing")}</Badge>
      )}
      <Button size="sm" variant="ghost" icon={<Upload size={14} />} loading={upload.isPending} onClick={() => input.current?.click()}>
        {doc ? t("admin.permitDocuments.replace") : t("admin.permitDocuments.upload")}
      </Button>
      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate({ file, subject_kind: subjectKind, subject_key: subjectKey, doc_kind: docKind });
          e.target.value = "";
        }}
      />
    </span>
  );
}

/** An equipment entry's datasheet: the maker's page (link) and the PDF plan sets append (view, upload, replace). */
export function EquipmentDatasheet({ item }: { item: S["PermitEquipment"] }) {
  const input = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return rawFetch(`/api/v1/admin/permit-equipment/${item.id}/datasheet`, { method: "POST", body: form });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-permit-equipment"] });
      qc.invalidateQueries({ queryKey: ["plan-set-preview"] });
      toast(t("admin.permitDocuments.datasheetStored", { manufacturer: item.manufacturer, model: item.model }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const view = async () => {
    const blob = await (await rawFetch(item.datasheet_file_url!)).blob();
    window.open(URL.createObjectURL(blob), "_blank", "noopener");
  };
  if (item.generic) return <span className="small muted">{t("admin.permitDocuments.notNeeded")}</span>;
  return (
    <span className="row" style={{ gap: 4, flexWrap: "wrap", alignItems: "center" }}>
      {item.datasheet_url && (
        <a className="small" href={item.datasheet_url} target="_blank" rel="noreferrer">
          {t("admin.permitDocuments.source")}
        </a>
      )}
      {item.datasheet_file_url ? <Badge tone="ok">PDF</Badge> : <Badge tone="warn">{t("admin.permitDocuments.noPdf")}</Badge>}
      {item.datasheet_file_url && <Button size="sm" variant="ghost" icon={<Eye size={14} />} aria-label={t("admin.permitDocuments.viewDatasheet")} onClick={() => void view()} />}
      <Button size="sm" variant="ghost" icon={<Upload size={14} />} loading={upload.isPending} onClick={() => input.current?.click()}>
        {item.datasheet_file_url ? t("admin.permitDocuments.replace") : t("admin.permitDocuments.upload")}
      </Button>
      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate(file);
          e.target.value = "";
        }}
      />
    </span>
  );
}

/** The racking catalogue's documents (rails, attachments, rapid-shutdown devices). */
export function AdminRackingDocuments({ docs }: { docs: Doc[] | undefined }) {
  const t = useT();
  const racking = useQuery({ queryKey: ["permit-racking"], queryFn: () => unwrap(api.GET("/v1/permit-racking")) });
  if (racking.isLoading) return <Spinner />;
  const groups: { title: MessageKey; kind: SubjectKind; items: S["PermitRackingItem"][]; extra?: { kind: DocKind; label: MessageKey } }[] = [
    { title: "admin.permitDocuments.kindRails", kind: "rail", items: racking.data?.rails ?? [], extra: { kind: "span_table", label: "admin.permitDocuments.spanTable" } },
    { title: "admin.permitDocuments.kindAttachments", kind: "attachment", items: racking.data?.attachments ?? [], extra: { kind: "attachment_detail", label: "admin.permitDocuments.detailPage" } },
    { title: "admin.permitDocuments.kindRsd", kind: "rsd", items: racking.data?.rsd_devices ?? [] },
  ];
  return (
    <Card title={t("admin.permitDocuments.rackingTitle")} description={t("admin.permitDocuments.rackingDescription")}>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>{t("admin.permitDocuments.colItem")}</th>
              <th>{t("admin.permitDocuments.colKind")}</th>
              <th>{t("admin.permitDocuments.colDatasheet")}</th>
              <th>{t("admin.permitDocuments.colMore")}</th>
            </tr>
          </thead>
          <tbody>
            {groups.flatMap((g) =>
              g.items
                .filter((i) => !i.key.startsWith("generic"))
                .map((i) => (
                  <tr key={`${g.kind}:${i.key}`}>
                    <td>
                      <b dir="auto">{i.label}</b>
                      {i.fastener && <div className="small muted" dir="auto">{i.fastener}</div>}
                    </td>
                    <td>{t(g.title)}</td>
                    <td>
                      <DocumentSlot subjectKind={g.kind} subjectKey={i.key} docKind="datasheet" docs={docs} />
                    </td>
                    <td>{g.extra ? <DocumentSlot subjectKind={g.kind} subjectKey={i.key} docKind={g.extra.kind} docs={docs} label={g.extra.label} /> : <span className="muted">—</span>}</td>
                  </tr>
                )),
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
